import { createContext, type ReactNode, useContext } from "react";
import type { RecordObject } from "./objects";

const Context = createContext<RecordObject[]>([]);

/** The records a report declares, for blocks that link one to another. */
export const RecordObjectsProvider = ({
  objects,
  children,
}: {
  objects: RecordObject[];
  children: ReactNode;
}) => <Context.Provider value={objects}>{children}</Context.Provider>;

export const useRecordObjects = () => useContext(Context);

/** The run a report page shows, for blocks that read its findings. */
export interface ReviewScope {
  businessId: string;
  reportId: string;
  runId: string;
  /** False for a draft's sample: its flagged rows never become findings. */
  reviewsFindings: boolean;
  /** The background review is still writing the run's findings. */
  reviewing: boolean;
}

/**
 * Whether a table of flagged rows lists the run's findings: only once the
 * run has them. Until then (a draft's sample, or a review still running)
 * it is a table of the flagged rows.
 */
export const listsFindings = (scope: ReviewScope | null) =>
  !!scope && scope.reviewsFindings && !scope.reviewing;

const ScopeContext = createContext<ReviewScope | null>(null);

export const ReviewScopeProvider = ({
  scope,
  children,
}: {
  scope: ReviewScope | null;
  children: ReactNode;
}) => <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>;

export const useReviewScope = () => useContext(ScopeContext);
